import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listen, platform } from "./tauri-web";
import { openFileDialog, saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import {
  store, relativeDue, computeDeckStats, buildStudyQueue, computeStreak,
  type Deck, type Card, type DeckStats, type ReviewLogEntry, type StreakInfo,
} from "./store";
import { syncReminders, type Reminder } from "./reminders";
import { gradeCard, previewInterval, GRADE_LABELS, STATE_NEW, STATE_LEARNING, STATE_RELEARNING, STATE_REVIEW, isDue, type Grade } from "./fsrs";
import { generateCards, academicRefusal, openWebLogin, BrainError, type BrainErrorCode, type GeneratedCard } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { buildExport } from "./export";
import { BackupMenu } from "./BackupMenu";
import Chat from "./chat/Chat";
import glyphReviewDoor from "./assets/glyph-review-door.svg";
import glyphGradeAgain from "./assets/glyph-grade-again.svg";
import glyphGradeHard from "./assets/glyph-grade-hard.svg";
import glyphGradeGood from "./assets/glyph-grade-good.svg";
import glyphGradeEasy from "./assets/glyph-grade-easy.svg";
import glyphLeech from "./assets/glyph-leech.svg";
import emptyNoDecks from "./assets/empty-no-decks.svg";
import emptyCaughtUp from "./assets/empty-caught-up.svg";
import {
  Sparkles, Trash2, X, Lock, BookOpen, WifiOff, RefreshCw, Camera, Layers,
  Clock, AlertCircle, Plus, Copy, Check, Coins, Play, Pencil, BarChart2, GraduationCap,
} from "lucide-react";

const LIST_CAP = 50000;

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

// Anki-style spaced-repetition breakdown — each deck shows its New / Learning / Due counts in the
// convention colours (new=blue, learning=amber, due=green). This SRS status row is the study-app signature.
function SrsCount({ n, color, label }: { n: number; color: string; label: string }) {
  if (n <= 0) return null;
  return (
    <span className="row tnum" style={{ gap: 3, fontSize: 10, fontWeight: 700, color, alignItems: "center" }}>
      <span aria-hidden style={{ width: 7, height: 7, borderRadius: 999, background: color, display: "inline-block" }} />
      {n}<span style={{ fontWeight: 500, opacity: 0.75, marginInlineStart: 1 }}>{label}</span>
    </span>
  );
}

/**
 * 'say it first, then check yourself' — a card turning over.
 * Replaces the lucide `Sparkles` that 20 of the fleet's 26 chat launchers rendered in common;
 * the button outline changes with it, because one glyph in two colours is still one control
 * (Guideline 4.3(a)).
 */
function FlipMark({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="4" y="6" width="16" height="12" rx="2.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 6v12" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2.2" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2 #62, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-flashcards://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // oriora-flashcards://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Oriora Flashcards"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <LoadingGate />;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function LoadingGate() {
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "1.5rem" }}>
      <div style={{ textAlign: "center" }}>
        <div className="empty-art" style={{ marginBottom: "0.9rem" }}><Layers size={34} /></div>
        <div className="muted row" style={{ justifyContent: "center", gap: "0.4rem", fontSize: "var(--fs-sm)" }}>
          <Sparkles size={13} className="pulse-soft" style={{ color: "var(--accent)" }} /> Opening your decks…
        </div>
      </div>
    </main>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card fade-up empty-glow" style={{ width: "100%", maxWidth: 380, padding: "2.25rem 2rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
        <div className="empty-art" style={{ marginBottom: "0.9rem" }}><Layers size={34} /></div>
        <div className="eyebrow" style={{ justifyContent: "center", marginBottom: "0.35rem" }}>Oriora Flashcards</div>
        <h1 className="serif" style={{ fontSize: "1.6rem", margin: "0 0 0.5rem" }}>Sign in with Oriora</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, marginBottom: "1.4rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. Your decks, cards and
          review history live on this device; only the notes you choose to turn into cards run
          through your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary btn-pill" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos("oriora-flashcards");
              else if (p === "macos") await signInMac("oriora-flashcards");
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/oriora-flashcards/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? (<><Sparkles size={14} className="pulse-soft" /> Signing in…</>) : "Sign in with Oriora"}
        </button>
        <div className="muted row" style={{ justifyContent: "center", gap: "0.3rem", fontSize: "var(--fs-xxs)", marginTop: "1rem" }}>
          <Lock size={11} /> Your cards live on this device — you choose what's sent or backed up
        </div>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  // Study streak (parity #14) — derived from the review log we already keep. No AI, nothing billed.
  const [streak, setStreak] = useState<StreakInfo | null>(null);
  const [decks, setDecks] = useState<Deck[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [showDeckForm, setShowDeckForm] = useState(false);
  const [editingDeck, setEditingDeck] = useState<Deck | null>(null);
  const [cardFormFor, setCardFormFor] = useState<{ deck: Deck; card: Card | null } | null>(null);
  const [generateFor, setGenerateFor] = useState<Deck | null>(null);
  const [browseFor, setBrowseFor] = useState<Deck | null>(null);
  const [studyFor, setStudyFor] = useState<Deck | null>(null);
  const [statsFor, setStatsFor] = useState<Deck | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Deck | null>(null);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not
  // change any existing screen. onChanged → reload() refreshes the list after a chat-driven change.
  const [chatOpen, setChatOpen] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // CSV export uses the desktop save-file dialog, which doesn't exist on iOS — hide the
  // affordance there. Default to showing it if platform() can't be resolved, correct after mount.
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => { try { setIsDesktop(platform() !== "ios" && platform() !== "android"); } catch { /* keep default */ } }, []);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const exportRef = useRef<(() => void) | null>(null);

  const reload = useCallback(async () => {
    const [d, c] = await Promise.all([store.listDecks(), store.listCards(undefined, { limit: LIST_CAP })]);
    setDecks(d);
    setCards(c);
    // Streak rides the same reload so it refreshes the moment a session ends.
    try { setStreak(computeStreak(await store.listReviewLog(undefined, { limit: 5000 }))); } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // On-device review reminders: for each deck with cards coming due, ONE local notification at
  // 09:00 local on the day its soonest card falls due, saying how many are ready by then. Built
  // straight off the FSRS `due` timestamps already on the cards — nothing invented; a deck with
  // nothing upcoming gets no reminder. One per deck, so we never approach the OS pending limit.
  // Best-effort + contextual (permission only when there's something future to schedule) and
  // never blocks the UI (see reminders.ts). Re-syncs whenever cards/decks change.
  useEffect(() => {
    const nowMs = Date.now();
    const reminders: Reminder[] = [];
    for (const deck of decks) {
      const deckCards = cards.filter((c) => c.deck_id === deck.id);
      let soonest: number | null = null;
      for (const c of deckCards) {
        const t = new Date(c.due).getTime();
        if (Number.isNaN(t) || t <= nowMs) continue; // only future due dates
        if (soonest === null || t < soonest) soonest = t;
      }
      if (soonest === null) continue; // nothing upcoming for this deck — never invent a date
      const day = new Date(soonest);
      const endOfDay = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999).getTime();
      const readyThatDay = deckCards.filter((c) => {
        const t = new Date(c.due).getTime();
        return !Number.isNaN(t) && t <= endOfDay; // due at or before end of that day
      }).length;
      const at = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0, 0, 0); // 09:00 local
      reminders.push({
        key: `due-${deck.id}`,
        title: "Cards ready to review",
        body: `${readyThatDay} card${readyThatDay === 1 ? "" : "s"} ready to review in ${deck.name}.`,
        at,
      });
    }
    void syncReminders(reminders);
  }, [cards, decks]);

  const now = new Date();
  const cardsByDeck = useMemo(() => {
    const m = new Map<string, Card[]>();
    for (const c of cards) {
      const arr = m.get(c.deck_id);
      if (arr) arr.push(c); else m.set(c.deck_id, [c]);
    }
    return m;
  }, [cards]);
  const dueTotal = useMemo(() => buildStudyQueue(cards, now, 9999).length, [cards]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Ledger row 2b — ORDERING. `list_decks` serves `ORDER BY name COLLATE NOCASE ASC`, so an empty
   * deck called "Anatomy" sat above one with forty cards due. The app is a spaced-repetition
   * system: the only question its home screen has to answer is "what do I study now?", and it
   * already knows — db.rs even builds `idx_cards_due ON cards(due)` to make exactly this lookup
   * fast, then the screen ignored it.
   *
   * Decks with something due come first, most due first. Decks with nothing due keep their
   * alphabetical order among themselves — once nothing is owed, a name is the useful handle.
   */
  const rankedDecks = useMemo(() => {
    const dueOf = (id: string) => buildStudyQueue(cardsByDeck.get(id) ?? [], now, 9999).length;
    return [...decks]
      .map((d) => ({ d, due: dueOf(d.id) }))
      .sort((a, b) => b.due - a.due || a.d.name.localeCompare(b.d.name, undefined, { sensitivity: "base" }))
      .map((x) => x.d);
  }, [decks, cardsByDeck]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Leeches — the cards you keep forgetting. `lapses` is persisted on every card by FSRS and,
   * before this, appeared nowhere except a line in the browse list. It is the one piece of study
   * data that says where the time is actually going: a card lapsed six times has cost six reviews
   * and still is not known. Stated as a plain count, with no instruction attached.
   */
  const leeches = useMemo(
    () => cards.filter((c) => c.lapses >= 3).sort((a, b) => b.lapses - a.lapses).slice(0, 3),
    [cards],
  );

  // Native-menu actions (Actions menu in the macOS menu bar): Add deck / Study / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "add": setEditingDeck(null); setShowDeckForm(true); break;
        case "study": {
          // Open the first deck with something due.
          void (async () => {
            const [d, c] = await Promise.all([store.listDecks(), store.listCards(undefined, { limit: LIST_CAP })]);
            const n = new Date();
            const target = d.find((deck) => buildStudyQueue(c.filter((x) => x.deck_id === deck.id), n).length > 0);
            if (target) setStudyFor(target);
          })();
          break;
        }
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const doExport = useCallback(() => {
    if (cards.length === 0) return;
    const { csv, filename } = buildExport(decks, cards);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [decks, cards]);
  exportRef.current = doExport;

  async function doDeleteDeck(id: string) {
    setConfirmDelete(null);
    await store.deleteDeck(id);
    await reload();
  }

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      // Safe-area aware so content clears the iPhone notch/home-indicator (viewport-fit=cover).
      padding: "max(1.5rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
      <header className="row" style={{ justifyContent: "space-between", marginBottom: "1.1rem", alignItems: "flex-start" }}>
        <div className="row" style={{ gap: "0.55rem" }}>
          <span className="thumb" style={{ width: 38, height: 38, borderRadius: 11 }}><Layers size={20} /></span>
          <h1 className="serif" style={{ fontSize: "1.5rem", margin: 0 }}>Flashcards</h1>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <BackupMenu
            appName="Flashcards" fileSlug="flashcards" appScheme="oriora-flashcards"
            hasData={cards.length > 0} onRestored={reload}
            onExportCsv={isDesktop && cards.length > 0 ? doExport : undefined}
          />
          <AccountMenu email={email} appSlug="oriora-flashcards" appName="Oriora Flashcards" onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </header>

      {streak && streak.best > 0 && (
        <div className="card" style={{ padding: "0.75rem 0.95rem", marginBottom: "0.9rem" }}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div className="streak-row">
                <span className="streak-num">{streak.current}</span>
                <span className="muted" style={{ fontSize: "var(--fs-sm)" }}>
                  day{streak.current === 1 ? "" : "s"} in a row
                </span>
              </div>
              <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "0.15rem" }}>
                {streak.studiedToday ? "Studied today — nice." : "Study once today to keep it going."}
                {streak.best > streak.current ? ` · best ${streak.best}` : ""}
              </div>
            </div>
            <div className="streak-dots" aria-label={`Last 7 days: ${streak.last7.filter(Boolean).length} studied`}>
              {streak.last7.map((on, i) => <span key={i} className={`streak-dot${on ? " on" : ""}`} />)}
            </div>
          </div>
        </div>
      )}

      {offline && (
        <div role="status" className="notice notice-warn fade-up" style={{ marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Making cards from notes is paused while you're offline — studying still works, and your decks stay on your device unless you back them up.</span>
        </div>
      )}

      {/* R1 — THE REVIEW DOOR: one front door into today's due queue. */}
      <CollectionHero decks={decks.length} cards={cards.length} due={dueTotal}
        onEnter={() => {
          const target = rankedDecks.find((d) => buildStudyQueue(cardsByDeck.get(d.id) ?? [], now, 9999).length > 0);
          if (target) setStudyFor(target);
        }} />

      {/* READY-STATE (2026-09-19): the door above is the promise ("what should I review now"),
          but with nothing in the box it reads "No cards yet" and the SCHEDULER — the row the
          matrix rates BETTER than Anki's 1987 SM-2 — was invisible. The queue is now drawn with
          its own states, and one line puts a real card into it. */}
      {cards.length === 0 && <EmptyQueue onFirstCard={reload} />}

      <div style={{ marginTop: "1rem" }}><LeechCard leeches={leeches} /></div>

      <div className="sec-head">
        <h2><BookOpen size={17} style={{ color: "var(--accent)" }} /> Your decks {decks.length > 0 && <span className="muted tnum" style={{ fontWeight: 400 }}>({decks.length})</span>}</h2>
        <button className="btn btn-sm btn-pill" onClick={() => { setEditingDeck(null); setShowDeckForm(true); }}>
          <Plus size={14} /> New deck
        </button>
      </div>

      {decks.length === 0 ? (
        <div className="card fade-up empty-glow" style={{ padding: "2.75rem 1.75rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
          <div className="empty-art" style={{ width: 180, height: 135 }}><img src={emptyNoDecks} alt="" aria-hidden="true" /></div>
          <div className="serif" style={{ fontSize: "1.25rem", margin: "0.85rem 0 0.4rem" }}>Make your first deck</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.55, maxWidth: 320, margin: "0 auto" }}>
            Type cards by hand, paste your notes, or snap a photo of a page — then study with
            spaced repetition that shows each card right before you'd forget it.
          </div>
          <button className="btn btn-primary btn-pill" style={{ marginTop: "1.25rem" }} onClick={() => { setEditingDeck(null); setShowDeckForm(true); }}>
            <Plus size={16} /> New deck
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.55rem" }}>
          {rankedDecks.map((d) => (
            <DeckTile
              key={d.id} deck={d} cards={cardsByDeck.get(d.id) ?? []}
              onStudy={() => setStudyFor(d)}
              onAddCard={() => setCardFormFor({ deck: d, card: null })}
              onGenerate={() => setGenerateFor(d)}
              onBrowse={() => setBrowseFor(d)}
              onStats={() => setStatsFor(d)}
              onEdit={() => { setEditingDeck(d); setShowDeckForm(true); }}
              onDelete={() => setConfirmDelete(d)}
            />
          ))}
        </div>
      )}

      {showDeckForm && (
        <DeckForm editing={editingDeck} onClose={() => { setShowDeckForm(false); setEditingDeck(null); }}
          onSaved={() => { setShowDeckForm(false); setEditingDeck(null); void reload(); }} />
      )}

      {cardFormFor && (
        <CardForm deck={cardFormFor.deck} editing={cardFormFor.card}
          onClose={() => setCardFormFor(null)}
          onSaved={() => { setCardFormFor(null); void reload(); }} />
      )}

      {generateFor && (
        <GenerateSheet deck={generateFor} offline={offline}
          onClose={() => setGenerateFor(null)}
          onSaved={() => { setGenerateFor(null); void reload(); }} />
      )}

      {browseFor && (
        <BrowseModal deck={browseFor} onClose={() => setBrowseFor(null)}
          onEditCard={(c) => { setBrowseFor(null); setCardFormFor({ deck: browseFor, card: c }); }}
          onChanged={() => void reload()} />
      )}

      {studyFor && (
        <StudyModal deck={studyFor} cards={cardsByDeck.get(studyFor.id) ?? []}
          onClose={() => { setStudyFor(null); void reload(); }} />
      )}

      {statsFor && (
        <StatsModal deck={statsFor} cards={cardsByDeck.get(statsFor.id) ?? []} onClose={() => setStatsFor(null)} />
      )}

      {confirmDelete && (
        <ModalShell title="Delete deck?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.55 }}>
            Delete "<strong style={{ color: "var(--ink-1)" }}>{confirmDelete.name}</strong>" and its {confirmDelete.card_count} card{confirmDelete.card_count === 1 ? "" : "s"} (including their review history)? This can't be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-danger btn-pill" onClick={() => doDeleteDeck(confirmDelete.id)}>Delete</button>
          </div>
        </ModalShell>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the list. */}
      {!chatOpen && (
        <button className="rbc-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <FlipMark size={19} />
        </button>
      )}
      <aside className={`rbc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="rbc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to add, make cards, or ask</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

/**
 * The cards you keep forgetting — ledger row 2b.
 *
 * FSRS persists `lapses` on every card (a lapse = you had learned it and then failed it again).
 * It is the one number that says where study time is actually going: a card lapsed six times has
 * cost six reviews and still is not known. Before this it appeared nowhere but a line inside the
 * browse list, so it could only be found by someone already looking for it.
 *
 * Stated as a count, with no instruction attached — whether to reword the card, split it, or drop
 * it is the learner's call, and the app has no basis to pick for them.
 */
// ── THE QUEUE, DRAWN — the scheduler shown before the first card ─────────────────
// PARITY-MATRIX row 2: every card carries its own FSRS-4.5 state (stability, difficulty, reps,
// lapses) and the grade you give moves it. At zero cards none of that was on screen. The three
// states are the real ones from `fsrs.ts` (new · learning · review), and the grade names are the
// real four, in the app's own grading ramp.
function EmptyQueue({ onFirstCard }: { onFirstCard: () => void }) {
  const states: { label: string; note: string; color: string }[] = [
    { label: "New", note: "never shown yet — due the moment it exists", color: "var(--ink-2)" },
    { label: "Learning", note: "minutes apart, until it sticks", color: "var(--grade-hard)" },
    { label: "Review", note: "days, then weeks, as its stability grows", color: "var(--grade-good)" },
  ];
  return (
    <>
      <section className="fc-queue" aria-label="How the queue works">
        <div className="fc-queue-lbl">The queue</div>
        <div className="fc-queue-states">
          {states.map((st) => (
            <div key={st.label} className="fc-queue-state">
              <span className="fc-queue-dot" style={{ background: st.color }} aria-hidden />
              <span className="fc-queue-name">{st.label}</span>
              <span className="fc-queue-count tnum">0</span>
              <span className="fc-queue-note">{st.note}</span>
            </div>
          ))}
        </div>
        <p className="fc-queue-body">
          Each card keeps its own <b>stability</b> — how long it is expected to stay remembered —
          plus a difficulty, a count of reps and of lapses. Grading it <span className="fc-g again">Again</span>
          <span className="fc-g hard">Hard</span><span className="fc-g good">Good</span>
          <span className="fc-g easy">Easy</span> moves those numbers and sets the next date. That is
          the whole schedule — the dates come from the card, not from a calendar you have to keep up with.
        </p>
        <p className="fc-queue-foot">
          Studying is free and runs on this device — no subscription and no ads. The only step that
          costs anything is making cards out of notes or a photographed page, and only when you ask.
        </p>
      </section>

      <FirstCard onSaved={onFirstCard} />
    </>
  );
}

// ── THE DOOR — one card, straight into the scheduler ────────────────────────────
// The existing way in is: make a deck (a modal), then add a card (another modal). This writes the
// deck and the card together, and because a new card is due immediately the review door above
// turns from "No cards yet" into "1 due" as soon as it saves.
function FirstCard({ onSaved }: { onSaved: () => void }) {
  const [deckName, setDeckName] = useState("");
  const [front, setFront] = useState("");
  const [back, setBack] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ready = front.trim() !== "" && back.trim() !== "";

  async function save() {
    if (!ready || saving) return;
    setSaving(true); setErr(null);
    try {
      const decks = await store.listDecks();
      const deck = decks[0] ?? (await store.createDeck({ name: deckName.trim() || "My first deck" }));
      await store.createCard({ deck_id: deck.id, front: front.trim(), back: back.trim(), source: "typed" });
      setFront(""); setBack("");
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="fc-first" aria-label="Write the first card">
      <div className="fc-first-lbl">Write one card</div>
      <input className="fc-first-in" placeholder="Front — the question or cue" value={front}
        onChange={(e) => { setFront(e.target.value); setErr(null); }}
        onKeyDown={(e) => { if (e.key === "Enter") void save(); }} aria-label="Front" />
      <input className="fc-first-in" placeholder="Back — the answer" value={back}
        onChange={(e) => { setBack(e.target.value); setErr(null); }}
        onKeyDown={(e) => { if (e.key === "Enter") void save(); }} aria-label="Back" />
      <input className="fc-first-in fc-first-deck" placeholder="Deck name (optional)" value={deckName}
        onChange={(e) => setDeckName(e.target.value)} aria-label="Deck name (optional)" />
      <div className="fc-first-hint">It enters the queue as <b>New</b> and is due straight away — the door above will say so.</div>
      {err && <div className="fc-first-err">{err}</div>}
      <button className="btn btn-primary fc-first-save" disabled={!ready || saving} onClick={save}>
        {saving ? "Saving…" : "Add it to the queue"}
      </button>
    </section>
  );
}

function LeechCard({ leeches }: { leeches: Card[] }) {
  if (leeches.length === 0) return null;
  return (
    <div className="leech-signal fade-up" style={{ marginBottom: "1.1rem" }}>
      <div className="leech-signal-head"><img src={glyphLeech} alt="" aria-hidden="true" /> Keeps slipping</div>
      {leeches.map((c) => (
        <div key={c.id} className="leech-row">
          <span className="leech-row-front">{c.front}</span>
          <span className="leech-row-count tnum">forgotten {c.lapses}×</span>
        </div>
      ))}
    </div>
  );
}

/** R1 — the Review Door: the home's ONE front door into today's due queue. */
function CollectionHero({ decks, cards, due, onEnter }: { decks: number; cards: number; due: number; onEnter: () => void }) {
  const done = due === 0;
  return (
    <button type="button" className={`review-door fade-up${done ? " done" : ""}`} onClick={onEnter} disabled={done}
      aria-label={done ? "Nothing due right now" : `Review ${due} due card${due === 1 ? "" : "s"}`}>
      <img className="review-door-glyph" src={glyphReviewDoor} alt="" aria-hidden="true" />
      <span className="eyebrow review-door-eyebrow"><GraduationCap size={13} /> Today’s review</span>
      <div className="review-door-count tnum">
        {done ? (cards === 0 ? "No cards yet" : "All caught up") : `${due} due`}
      </div>
      <div className="review-door-meta">
        {cards === 0 ? "Make a deck to get started" : done ? `${cards} card${cards === 1 ? "" : "s"} across ${decks} deck${decks === 1 ? "" : "s"} — every next date is set` : `${cards} card${cards === 1 ? "" : "s"} across ${decks} deck${decks === 1 ? "" : "s"}`}
      </div>
      {!done && (
        <span className="review-door-cta">Start reviewing <span className="review-door-arrow">→</span></span>
      )}
    </button>
  );
}

function DeckTile({ deck, cards, onStudy, onAddCard, onGenerate, onBrowse, onStats, onEdit, onDelete }: {
  deck: Deck; cards: Card[];
  onStudy: () => void; onAddCard: () => void; onGenerate: () => void; onBrowse: () => void;
  onStats: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const now = new Date();
  const due = buildStudyQueue(cards, now, 9999).length;
  const fresh = cards.filter((c) => c.state === STATE_NEW).length;
  const learning = cards.filter((c) => c.state === STATE_LEARNING || c.state === STATE_RELEARNING).length;
  const dueReview = cards.filter((c) => c.state === STATE_REVIEW && isDue(c, now)).length;
  return (
    // 🔴 FITS-ON-SCREEN (ledger row 1c). The tile is thumb + title + FIVE icon buttons. On a 430pt
    // phone the icons and thumb take ~230pt, so the title was ellipsised down to "Pha…" and
    // "Anatomy ba…" — the deck name, the one thing you scan for, was the first casualty. Caught by
    // actually looking at the store screenshot, which is what row 4 is for.
    // Letting the tile WRAP means the action row drops to its own line when it cannot fit beside
    // the name, instead of squeezing it. Nothing is hidden and no handler changes.
    <div className="deck-row" style={{ flexWrap: "wrap", rowGap: "0.35rem" }}>
      <button className="deck-row-thumb" onClick={onEdit} aria-label={`Edit ${deck.name}`}>
        <BookOpen size={22} />
      </button>
      <button style={{ flex: "1 1 190px", minWidth: 0, cursor: "pointer", background: "none", border: "none", textAlign: "left", padding: 0 }} onClick={onBrowse}>
        <div className="row" style={{ gap: "0.4rem", flexWrap: "nowrap" }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", color: "var(--ink-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{deck.name}</div>
          {due > 0 && <span className="deck-row-due-badge tnum" style={{ flexShrink: 0 }}>{due} due</span>}
        </div>
        <div className="row" style={{ gap: "0.6rem", marginTop: 5, flexWrap: "wrap", alignItems: "center" }}>
          <SrsCount n={fresh} color="#3b82f6" label="new" />
          <SrsCount n={learning} color="var(--warning)" label="learning" />
          <SrsCount n={dueReview} color="var(--good)" label="due" />
          {fresh + learning + dueReview === 0 && cards.length > 0 && (
            <span className="row" style={{ gap: 3, fontSize: 10, fontWeight: 600, color: "var(--good)" }}><Check size={10} /> all caught up</span>
          )}
          <span className="row tnum" style={{ gap: 3, fontSize: 10, color: "var(--ink-3)" }}>
            <Layers size={10} /> {cards.length}
          </span>
          {deck.description && (
            <span style={{ fontSize: 10, color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{deck.description}</span>
          )}
        </div>
      </button>
      <div className="row" style={{ gap: 2, flexShrink: 0, marginInlineStart: "auto" }}>
        <IconBtn title="Study" on={due > 0} onClick={onStudy}><Play size={15} /></IconBtn>
        <IconBtn title="Add a card" onClick={onAddCard}><Plus size={15} /></IconBtn>
        <IconBtn title="Make cards from notes" onClick={onGenerate}><Sparkles size={15} /></IconBtn>
        <IconBtn title="Deck stats" onClick={onStats}><BarChart2 size={15} /></IconBtn>
        <IconBtn title="Delete deck" danger onClick={onDelete}><Trash2 size={15} /></IconBtn>
      </div>
    </div>
  );
}

function IconBtn({ title, onClick, children, on, danger }: { title: string; onClick: () => void; children: React.ReactNode; on?: boolean; danger?: boolean }) {
  return (
    <button className={`icon-btn${on ? " on" : ""}${danger ? " danger" : ""}`} title={title} aria-label={title}
      onClick={(ev) => { ev.stopPropagation(); onClick(); }}>
      {children}
    </button>
  );
}

// ── Study — the FSRS due-queue, graded again/hard/good/easy, all on-device ───────
// QUIZ MODE (parity #13) — the leader's headline ("Learn" / practice tests). Flip-cards test
// RECOGNITION ("did I know it?" — self-graded, easy to fool yourself); a quiz tests RECALL, which is
// what an exam actually asks. Distractors are other cards' answers FROM THE SAME DECK, so nothing is
// invented and the whole mode costs nothing: no AI call, no billing, pure client-side.
function buildOptions(current: Card, pool: Card[]): string[] {
  const seen = new Set([current.back.trim().toLowerCase()]);
  const distractors: string[] = [];
  // Prefer same-tag cards — a plausible wrong answer makes the question worth asking.
  const ranked = [...pool]
    .filter((c) => c.id !== current.id)
    .sort((a, b) => Number(b.tag === current.tag) - Number(a.tag === current.tag));
  for (const c of ranked) {
    const k = c.back.trim().toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k); distractors.push(c.back.trim());
    if (distractors.length === 3) break;
  }
  const opts = [current.back.trim(), ...distractors];
  // Deterministic-enough shuffle; the card id keeps the order stable across re-renders.
  const seed = current.id.split("").reduce((a, ch) => a + ch.charCodeAt(0), 0);
  return opts
    .map((v, i) => ({ v, k: (seed * (i + 7)) % 97 }))
    .sort((a, b) => a.k - b.k)
    .map((x) => x.v);
}

function StudyModal({ deck, cards, onClose }: { deck: Deck; cards: Card[]; onClose: () => void }) {
  const [queue, setQueue] = useState<Card[]>(() => buildStudyQueue(cards));
  const [revealed, setRevealed] = useState(false);
  const [done, setDone] = useState(0);
  const [lapses, setLapses] = useState(0);
  const current = queue[0] ?? null;
  // Quiz mode needs >=3 distinct answers in the deck: fewer makes the choice a coin flip.
  const quizPossible = useMemo(
    () => new Set(cards.map((c) => c.back.trim().toLowerCase()).filter(Boolean)).size >= 3,
    [cards],
  );
  const [quiz, setQuiz] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const options = useMemo(
    () => (quiz && current ? buildOptions(current, cards) : []),
    [quiz, current, cards],
  );
  const correct = picked != null && current != null && picked.trim().toLowerCase() === current.back.trim().toLowerCase();

  async function grade(g: Grade) {
    if (!current) return;
    const now = new Date();
    const { next, scheduledDays, elapsedDays } = gradeCard(current, g, now);
    await store.applyReview(current.id, {
      ...next,
      grade: g,
      state_before: current.state,
      elapsed_days: Math.round(elapsedDays * 100) / 100,
      scheduled_days: Math.round(scheduledDays * 10000) / 10000,
      reviewed_at: now.toISOString(),
    });
    setDone((d) => d + 1);
    if (g === 1) setLapses((l) => l + 1);
    setRevealed(false);
    setPicked(null);
    setQueue((q) => {
      const rest = q.slice(1);
      // A minutes-scale step (learning/relearning) comes back later in THIS session.
      if (scheduledDays < 1) return [...rest, { ...current, ...next }];
      return rest;
    });
  }

  const total = done + queue.length;
  const GRADE_GLYPHS: Record<Grade, string> = { 1: glyphGradeAgain, 2: glyphGradeHard, 3: glyphGradeGood, 4: glyphGradeEasy };
  const GRADE_CLASS: Record<Grade, string> = { 1: "grade-btn-again", 2: "grade-btn-hard", 3: "grade-btn-good", 4: "grade-btn-easy" };
  return (
    <ModalShell title={`Study — ${deck.name}`} onCancel={onClose} wide>
      {current ? (
        <div className="study-stage">
          {quizPossible && (
            <div className="study-mode-row">
              <button className={`chip chip-mini${quiz ? "" : " selected"}`} onClick={() => { setQuiz(false); setPicked(null); }}>Flip cards</button>
              <button className={`chip chip-mini${quiz ? " selected" : ""}`} onClick={() => { setQuiz(true); setRevealed(false); setPicked(null); }}>Quiz me</button>
            </div>
          )}
          <div className="study-progress">
            <div className="study-progress-track"><div className="study-progress-fill" style={{ width: `${total > 0 ? (done / total) * 100 : 0}%` }} /></div>
            <span className="study-progress-label tnum">{queue.length} to go</span>
          </div>
          <div className="study-card">
            {current.tag && <span className="chip chip-mini study-tag" style={{ marginBottom: "0.6rem" }}>{current.tag}</span>}
            <div className="study-front">{current.front}</div>
            {revealed && <div className="study-back fade-up">{current.back}</div>}
          </div>
          {quiz && !revealed ? (
            /* QUIZ MODE — pick the answer. Grading is automatic and honest: right = Good,
               wrong = Again, so FSRS gets a real signal instead of a self-assessment. */
            <div className="quiz-options">
              {options.map((o) => {
                const isPicked = picked === o;
                const isRight = o.trim().toLowerCase() === current.back.trim().toLowerCase();
                const cls = picked == null ? "" : isRight ? " quiz-opt-right" : isPicked ? " quiz-opt-wrong" : " quiz-opt-dim";
                return (
                  <button key={o} className={`quiz-opt${cls}`} disabled={picked != null}
                    onClick={() => { setPicked(o); }}>
                    {o}
                  </button>
                );
              })}
              {picked != null && (
                <>
                  <div className={`quiz-verdict ${correct ? "is-right" : "is-wrong"}`}>
                    {correct ? "Correct" : `Not quite — it's “${current.back}”`}
                  </div>
                  <button className="btn btn-primary btn-pill" style={{ width: "100%", minHeight: 44 }}
                    onClick={() => void grade(correct ? 3 : 1)}>
                    Next card
                  </button>
                  <button className="btn btn-sm" style={{ width: "100%" }} onClick={() => setRevealed(true)}>
                    Grade it myself instead
                  </button>
                </>
              )}
            </div>
          ) : !revealed ? (
            <>
              <button className="btn btn-primary btn-pill study-flip-btn" onClick={() => setRevealed(true)}>Show answer</button>
              <div className="study-reveal-hint">Say it first — then check yourself.</div>
            </>
          ) : (
            <>
              <div className="grade-row">
                {([1, 2, 3, 4] as Grade[]).map((g) => (
                  <button key={g} className={`grade-btn ${GRADE_CLASS[g]}`} onClick={() => void grade(g)}>
                    <img src={GRADE_GLYPHS[g]} alt="" aria-hidden="true" />
                    <span className="grade-btn-label">{GRADE_LABELS[g]}</span>
                    <span className="grade-btn-interval tnum">{previewInterval(current, g)}</span>
                  </button>
                ))}
              </div>
              <div className="grade-hint">How well did you know it? The date under each answer is when you’d see this card again.</div>
            </>
          )}
          <div className="muted" style={{ fontSize: "var(--fs-xxs)", textAlign: "center" }}>
            Scheduled on this device with FSRS spaced repetition — no AI call, nothing sent.
          </div>
        </div>
      ) : (
        <div className="session-done">
          <img className="session-done-art" src={emptyCaughtUp} alt="" aria-hidden="true" />
          <div className="serif" style={{ fontSize: "1.35rem", margin: "0 0 0.35rem" }}>
            {done === 0 ? "Nothing due right now" : "Session done"}
          </div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.5 }}>
            {done === 0
              ? "Every card is scheduled for later — come back when the deck shows a due badge."
              : "The next dates are set."}
          </div>
          {done > 0 && (
            <div className="session-done-stats">
              <div><div className="session-stat-val tnum">{done}</div><div className="session-stat-lbl">reviews</div></div>
              <div><div className="session-stat-val tnum">{lapses}</div><div className="session-stat-lbl">to relearn</div></div>
              <div><div className="session-stat-val tnum">{done - lapses}</div><div className="session-stat-lbl">held</div></div>
            </div>
          )}
          <button className="btn btn-pill" style={{ marginTop: "1.1rem" }} onClick={onClose}>Close</button>
        </div>
      )}
    </ModalShell>
  );
}

// ── The ONE AI feature: paste notes (or scan a page) → study cards ───────────────
// Sends ONLY the notes text (typed/pasted, or produced by the ON-DEVICE OCR of a photographed
// page — the image never leaves the device). One billed call; the user reviews the preview and
// picks what to save. Each real-use path gets its own state: loading, success (preview + badge),
// refused (study-tool fence), insufficient-funds, BYOK gate, and network/unavailable.
type GenErr = { code: BrainErrorCode | "empty"; message: string };

function GenerateSheet({ deck, offline, onClose, onSaved }: { deck: Deck; offline: boolean; onClose: () => void; onSaved: () => void }) {
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState<GeneratedCard[] | null>(null);
  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<GenErr | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  // Live, instant client-side hint so the user sees the fence BEFORE pressing the button.
  const fenceHint = useMemo(() => academicRefusal(notes), [notes]);

  async function run() {
    setBusy(true); setErr(null); setPreview(null); setExcluded(new Set());
    try {
      const cards = await generateCards(notes);
      if (cards.length === 0) setErr({ code: "empty", message: "No study cards came out of that — try pasting fuller notes." });
      else setPreview(cards);
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "The request failed — please try again." });
    } finally { setBusy(false); }
  }

  async function saveCards() {
    if (!preview) return;
    const chosen = preview.filter((_, i) => !excluded.has(i));
    if (chosen.length === 0) return;
    setSaving(true);
    try {
      await store.createCards(deck.id, chosen.map((c) => ({ ...c, source: "notes" as const })));
      onSaved();
    } catch { setErr({ code: "unavailable", message: "Couldn't save the cards — try again." }); setSaving(false); }
  }

  // Photograph a notes page → on-device OCR (Apple Vision) → the recognized TEXT lands in the
  // notes box (review it, then Generate). The photo + recognized text stay on the device; only
  // the text you send reaches the brain (one billed call).
  async function runOcr(path: string) {
    setScanning(true); setScanMsg("Reading the photo…");
    try {
      let text = "";
      try { text = (await store.ocrImage(path)).trim(); }
      catch { setScanMsg("On-device scan isn't available here — paste the notes instead."); return; }
      if (!text) { setScanMsg("Couldn't read any text — try a clearer, straight-on photo, or paste the notes."); return; }
      setNotes((cur) => (cur.trim() ? `${cur.trim()}\n\n${text}` : text));
      setScanMsg("Read the photo — review the text below, then Generate.");
    } finally { setScanning(false); }
  }

  async function scanPhoto() {
    setScanMsg(null);
    // iOS: openFileDialog is a no-op → tap a hidden <input> to raise the native photo/camera
    // sheet. platform() is sync so .click() stays inside the tap's user-gesture (required).
    if (platform() === "ios") { photoInputRef.current?.click(); return; }
    try {
      const picked = await openFileDialog({ multiple: false, filters: [{ name: "Image", extensions: ["png", "jpg", "jpeg", "heic", "webp"] }] });
      if (picked && typeof picked === "string") { await runOcr(picked); }
    } catch { /* user cancelled */ }
  }

  async function onPhotoInputChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = ""; // reset so re-picking the same photo refires
    if (!file) return;
    setScanMsg("Reading…");
    try {
      const ext = (file.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const bytes = Array.from(new Uint8Array(await file.arrayBuffer()));
      const path = await store.savePhoto(bytes, ext);
      await runOcr(path);
    } catch { setScanMsg("Couldn't read that photo — try again or paste the notes."); }
  }

  const chosenCount = preview ? preview.length - excluded.size : 0;

  return (
    <ModalShell title="Make cards from notes" subtitle={deck.name} onCancel={onClose} wide>
      {!preview && (
        <>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.55, marginBottom: "0.8rem" }}>
            Paste your notes — lecture notes, a textbook passage, your own summary — and get back
            study cards that teach the ideas. You review every card before it's saved.
          </div>
          <textarea rows={7} placeholder="Paste your notes here…" value={notes} onChange={(e) => setNotes(e.target.value)}
            style={{ marginBottom: "0.6rem", resize: "vertical" }} autoFocus />
          <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.6rem" }}>
            <button type="button" className="btn btn-sm btn-pill row" style={{ gap: "0.35rem" }} onClick={scanPhoto} disabled={scanning || busy}>
              <Camera size={13} /> {scanning ? "Reading…" : "Scan a photo of notes"}
            </button>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)" }}>Read on your device — the photo never leaves it.</span> {/* §7b-verified — ipc.rs::ocr_image is on-device Apple Vision; only the recognised TEXT reaches /generate-cards, and runOcr never persists the photo, so Cloud Backup cannot relay it */}
          </div>
          {/* Hidden input raises the native iOS photo/camera sheet (desktop uses the file dialog). */}
          <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoInputChange} style={{ display: "none" }} />
          {scanMsg && <div className="muted" style={{ fontSize: "0.8rem", marginBottom: "0.6rem" }}>{scanMsg}</div>}

          {/* Study-tool fence — instant, client-side, BEFORE any billed call. */}
          {fenceHint && (
            <div className="notice notice-warn" style={{ marginBottom: "0.6rem" }}>
              <AlertCircle size={14} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
              <span>{fenceHint}</span>
            </div>
          )}

          <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>One paste = one Oriora call. Your decks stay on your device unless you back them up.</span>
            <button className="btn btn-primary btn-sm btn-pill" style={{ flexShrink: 0 }}
              disabled={busy || !!fenceHint || offline || notes.trim().length < 30} onClick={run}>
              {busy ? (<><Sparkles size={12} className="pulse-soft" /> Making cards…</>) : (<><Sparkles size={13} /> Generate</>)}
            </button>
          </div>

          {/* Loading state — a calm shimmer that reserves the result column (no layout shift). */}
          {busy && (
            <div aria-live="polite" style={{ marginTop: "0.85rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
              <div className="skeleton-block" style={{ height: 52 }} />
              <div className="skeleton-block" style={{ height: 52 }} />
              <div className="skeleton-line" style={{ width: "70%" }} />
            </div>
          )}

          {err && !busy && <GenError err={err} onRetry={run} />}
        </>
      )}

      {/* Success — the preview: tap a card to exclude it, then save the rest. */}
      {preview && (
        <div className="fade-up" aria-live="polite">
          <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.5, marginBottom: "0.6rem" }}>
            {preview.length} card{preview.length === 1 ? "" : "s"} from your notes — tap any card to leave it out, then save.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.45rem", maxHeight: 340, overflowY: "auto" }}>
            {preview.map((c, i) => {
              const off = excluded.has(i);
              return (
                <button key={i} type="button" onClick={() => setExcluded((s) => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; })}
                  className="card" style={{ textAlign: "left", padding: "0.7rem 0.8rem", cursor: "pointer", opacity: off ? 0.4 : 1, border: off ? "1px dashed var(--border-subtle)" : undefined }}>
                  <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem" }}>
                    <span style={{ fontWeight: 600, fontSize: "var(--fs-sm)", color: "var(--ink-1)" }}>{c.front}</span>
                    {c.tag && <span className="chip chip-mini" style={{ flexShrink: 0 }}>{c.tag}</span>}
                  </div>
                  <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 3, lineHeight: 1.5 }}>{c.back}</div>
                  {off && <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 4 }}>excluded — tap to include</div>}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.9rem" }}>
            <button className="btn btn-pill" onClick={() => { setPreview(null); setExcluded(new Set()); }} disabled={saving}>Back</button>
            <button className="btn btn-primary btn-pill" onClick={saveCards} disabled={saving || chosenCount === 0}>
              {saving ? (<><Sparkles size={13} className="pulse-soft" /> Saving…</>) : `Add ${chosenCount} card${chosenCount === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function GenError({ err, onRetry }: { err: GenErr; onRetry: () => void }) {
  // Study-tool fence (also enforced pre-call + server-side, surfaced here defensively).
  if (err.code === "refused_academic") {
    return (
      <div className="notice notice-warn fade-up" style={{ marginTop: 12 }}>
        <AlertCircle size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ color: "var(--ink-2)" }}>{err.message}</div>
      </div>
    );
  }
  // Insufficient funds — point to the Oriora account on the web, no amounts shown.
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Coins size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Add funds to keep making cards</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then try again.</div>
        </div>
      </div>
    );
  }
  // BYOK gate — Managed AI blocked for this app; needs a vendor key.
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Sparkles size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Session expired — needs re-auth.
  if (err.code === "unauthenticated") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Lock size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Empty result — gentle, no retry button.
  if (err.code === "empty") {
    return <div className="muted fade-up" style={{ fontSize: "var(--fs-sm)", marginTop: 12 }}>{err.message}</div>;
  }
  // Network / unavailable — recoverable, offer a retry.
  return (
    <div className="notice notice-err fade-up" style={{ marginTop: 12, justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-sm)" }}>{err.message}</span>
      <button className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onRetry}>
        <RefreshCw size={12} /> Retry
      </button>
    </div>
  );
}

// Ghost copy-button for text (DESIGN-SYSTEM §7.5). Swaps glyph → check + "Copied" for 1.5s.
function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* clipboard blocked — silently no-op */ }
  }, [text]);
  return (
    <button className="copy-btn" onClick={copy} aria-label={done ? "Copied" : label} title={done ? "Copied" : label}>
      <span aria-hidden>{done ? <Check size={13} /> : <Copy size={13} />}</span>
      <span className="copy-btn-lbl">{done ? "Copied" : label}</span>
      <span aria-live="polite" className="sr-only">{done ? "Copied to clipboard" : ""}</span>
    </button>
  );
}

function DeckForm({ editing, onClose, onSaved }: { editing: Deck | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) { setErr("Give the deck a name."); return; }
    setSaving(true); setErr(null);
    try {
      if (isEditing) await store.updateDeck(editing!.id, { name: name.trim(), description: description.trim() });
      else await store.createDeck({ name: name.trim(), description: description.trim() });
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg === "name_required" ? "Give the deck a name." : `Could not save — try again. (${msg})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit deck" : "New deck"} onCancel={onClose}>
      <label style={fieldLabel}>Name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Biology — chapter 4" autoFocus style={{ marginBottom: "1rem" }} />
      <label style={fieldLabel}>Description <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label>
      <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this deck covers" style={{ marginBottom: "1.25rem" }} />
      {err && <div className="notice notice-err" style={{ marginBottom: "1rem" }}><AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} /><span>{err}</span></div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn btn-pill" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary btn-pill" onClick={save} disabled={!name.trim() || saving}>{saving ? (<><Sparkles size={13} className="pulse-soft" /> Saving…</>) : isEditing ? "Update" : "Create deck"}</button>
      </div>
    </ModalShell>
  );
}

// CLOZE DELETION (parity #12) — Anki's signature card type, and the reason serious learners buy it.
// One sentence with [square brackets] around the bits to hide becomes ONE CARD PER BLANK, each
// showing the sentence with that word replaced by ____. Deterministic text work: no AI, nothing billed.
// The study loop and FSRS are untouched — a cloze card is just front/back with kind="cloze".
export function buildClozeCards(sentence: string): { front: string; back: string }[] {
  const text = sentence.trim();
  const re = /\[([^\]]+)\]/g;
  const hits: { word: string; start: number; end: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const w = m[1].trim();
    if (w) hits.push({ word: w, start: m.index, end: m.index + m[0].length });
  }
  if (!hits.length) return [];
  return hits.map((h) => {
    // Render the whole sentence with THIS blank hidden and the others shown plainly.
    let out = "";
    let cursor = 0;
    for (const x of hits) {
      out += text.slice(cursor, x.start);
      out += x === h ? "____" : x.word;
      cursor = x.end;
    }
    out += text.slice(cursor);
    return { front: out.trim(), back: h.word };
  });
}

function CardForm({ deck, editing, onClose, onSaved }: { deck: Deck; editing: Card | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [front, setFront] = useState(editing?.front ?? "");
  const [back, setBack] = useState(editing?.back ?? "");
  const [tag, setTag] = useState(editing?.tag ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Cloze authoring is only offered when CREATING (editing one card stays front/back).
  const [mode, setMode] = useState<"basic" | "cloze">("basic");
  const [sentence, setSentence] = useState("");
  const clozePreview = useMemo(() => buildClozeCards(sentence), [sentence]);

  async function saveCloze() {
    if (!clozePreview.length) { setErr("Put [square brackets] around at least one word to hide."); return; }
    setSaving(true); setErr(null);
    try {
      await store.createCards(deck.id, clozePreview.map((c) => ({ ...c, tag: tag.trim(), source: "typed" as const, kind: "cloze" as const })));
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  async function save() {
    if (!front.trim() || !back.trim()) { setErr("A card needs both sides."); return; }
    setSaving(true); setErr(null);
    try {
      if (isEditing) await store.updateCard(editing!.id, { front: front.trim(), back: back.trim(), tag: tag.trim() });
      else await store.createCard({ deck_id: deck.id, front: front.trim(), back: back.trim(), tag: tag.trim(), source: "typed" });
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit card" : "Add card"} subtitle={deck.name} onCancel={onClose} wide>
      {!isEditing && (
        <div className="row" style={{ gap: "0.4rem", marginBottom: "1rem" }}>
          <button className={`chip chip-mini${mode === "basic" ? " selected" : ""}`} onClick={() => { setMode("basic"); setErr(null); }}>Front / back</button>
          <button className={`chip chip-mini${mode === "cloze" ? " selected" : ""}`} onClick={() => { setMode("cloze"); setErr(null); }}>Fill in the blank</button>
        </div>
      )}

      {!isEditing && mode === "cloze" ? (
        <>
          <label style={fieldLabel}>Sentence <span className="muted" style={{ fontWeight: 400 }}>(put [square brackets] around each word to hide)</span></label>
          <textarea rows={3} value={sentence} onChange={(e) => setSentence(e.target.value)} autoFocus
            placeholder="e.g. The mitochondrion produces [ATP] through [oxidative phosphorylation]."
            style={{ marginBottom: "0.6rem", resize: "vertical" }} />
          <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginBottom: "1rem", lineHeight: 1.5 }}>
            One card per blank — the rest of the sentence stays visible as context.
          </div>
          {clozePreview.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <div className="muted" style={{ fontSize: "var(--fs-xxs)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.4rem" }}>
                {clozePreview.length} card{clozePreview.length === 1 ? "" : "s"}
              </div>
              {clozePreview.map((c, i) => (
                <div key={i} className="card" style={{ padding: "0.55rem 0.7rem", marginBottom: "0.4rem", fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>
                  {c.front} <span className="muted">→ <strong>{c.back}</strong></span>
                </div>
              ))}
            </div>
          )}
          <label style={fieldLabel}>Tag <span className="muted" style={{ fontWeight: 400 }}>(optional topic)</span></label>
          <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. cell respiration" style={{ marginBottom: "1.25rem" }} />
          {err && <div className="notice notice-err" style={{ marginBottom: "1rem" }}><AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} /><span>{err}</span></div>}
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary btn-pill" onClick={() => void saveCloze()} disabled={!clozePreview.length || saving}>
              {saving ? (<><Sparkles size={13} className="pulse-soft" /> Saving…</>) : `Save ${clozePreview.length || ""} card${clozePreview.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </>
      ) : (
      <>
      <label style={fieldLabel}>Front <span className="muted" style={{ fontWeight: 400 }}>(the question or cue)</span></label>
      <textarea rows={2} value={front} onChange={(e) => setFront(e.target.value)} placeholder="e.g. What is osmosis?" autoFocus style={{ marginBottom: "1rem", resize: "vertical" }} />
      <label style={fieldLabel}>Back <span className="muted" style={{ fontWeight: 400 }}>(the answer)</span></label>
      <textarea rows={3} value={back} onChange={(e) => setBack(e.target.value)} placeholder="e.g. The movement of water across a semi-permeable membrane…" style={{ marginBottom: "1rem", resize: "vertical" }} />
      <label style={fieldLabel}>Tag <span className="muted" style={{ fontWeight: 400 }}>(optional topic)</span></label>
      <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. cell transport" style={{ marginBottom: "1.25rem" }} />
      {err && <div className="notice notice-err" style={{ marginBottom: "1rem" }}><AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} /><span>{err}</span></div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn btn-pill" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary btn-pill" onClick={save} disabled={!front.trim() || !back.trim() || saving}>{saving ? (<><Sparkles size={13} className="pulse-soft" /> Saving…</>) : isEditing ? "Update" : "Save card"}</button>
      </div>
      </>
      )}
    </ModalShell>
  );
}

function BrowseModal({ deck, onClose, onEditCard, onChanged }: { deck: Deck; onClose: () => void; onEditCard: (c: Card) => void; onChanged: () => void }) {
  const [cards, setCards] = useState<Card[] | null>(null);
  const [query, setQuery] = useState("");
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const reload = useCallback(async () => { setCards(await store.listCards(deck.id, { limit: LIST_CAP })); }, [deck.id]);
  useEffect(() => { void reload(); }, [reload]);

  const filtered = useMemo(() => {
    if (!cards) return null;
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) => c.front.toLowerCase().includes(q) || c.back.toLowerCase().includes(q) || c.tag.includes(q));
  }, [cards, query]);

  async function del(id: string) {
    setConfirmId(null);
    await store.deleteCard(id);
    await reload();
    onChanged();
  }

  return (
    <ModalShell title="Cards" subtitle={deck.name} onCancel={onClose} wide>
      {cards === null ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }} aria-live="polite">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton-block" style={{ height: 52 }} />)}
        </div>
      ) : cards.length === 0 ? (
        <div style={{ textAlign: "center", padding: "1rem 0 1.25rem" }}>
          <div className="empty-art" style={{ width: 56, height: 56, borderRadius: 16 }}><Layers size={24} /></div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: "0.75rem", lineHeight: 1.5 }}>No cards yet.<br />Add one by hand or make cards from your notes.</div>
        </div>
      ) : (
        <>
          {cards.length > 5 && (
            <input placeholder="Search front, back, or tag…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ marginBottom: "0.7rem", fontSize: "var(--fs-sm)" }} />
          )}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem", maxHeight: 380, overflowY: "auto" }}>
            {(filtered ?? []).map((c) => (
              <div key={c.id} className="row" style={{ justifyContent: "space-between", gap: "0.6rem", padding: "0.6rem 0.7rem", background: "var(--elev-2)", borderRadius: 12 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="row" style={{ gap: "0.4rem", flexWrap: "nowrap" }}>
                    <span style={{ fontWeight: 600, fontSize: "var(--fs-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.front}</span>
                    {c.tag && <span className="chip chip-mini" style={{ flexShrink: 0 }}>{c.tag}</span>}
                  </div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.back}</div>
                  <div className="row tnum" style={{ gap: "0.5rem", fontSize: 10, color: "var(--ink-3)", marginTop: 3 }}>
                    <span><Clock size={9} style={{ verticalAlign: -1 }} /> due {relativeDue(c.due)}</span>
                    {c.reps > 0 && <span>{c.reps} review{c.reps === 1 ? "" : "s"}</span>}
                    {c.lapses > 0 && <span>{c.lapses} lapse{c.lapses === 1 ? "" : "s"}</span>}
                  </div>
                </div>
                <div className="row" style={{ gap: 2, flexShrink: 0 }}>
                  <IconBtn title="Edit card" onClick={() => onEditCard(c)}><Pencil size={14} /></IconBtn>
                  {confirmId === c.id ? (
                    <button className="btn btn-danger btn-sm btn-pill" onClick={() => void del(c.id)}>Sure?</button>
                  ) : (
                    <IconBtn title="Delete card" danger onClick={() => setConfirmId(c.id)}><Trash2 size={14} /></IconBtn>
                  )}
                </div>
              </div>
            ))}
            {filtered && filtered.length === 0 && (
              <div className="muted" style={{ fontSize: "var(--fs-sm)", textAlign: "center", padding: "0.75rem 0" }}>No cards match "{query}".</div>
            )}
          </div>
        </>
      )}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "1.25rem" }}>
        <button className="btn btn-pill" onClick={onClose}>Close</button>
      </div>
    </ModalShell>
  );
}

// Per-deck stats — every number computed on-device from the local cards + review log.
function StatsModal({ deck, cards, onClose }: { deck: Deck; cards: Card[]; onClose: () => void }) {
  const [log, setLog] = useState<ReviewLogEntry[] | null>(null);
  useEffect(() => { void store.listReviewLog(deck.id, { limit: 5000 }).then(setLog); }, [deck.id]);
  const stats: DeckStats | null = useMemo(() => (log === null ? null : computeDeckStats(cards, log)), [cards, log]);

  const copyText = stats
    ? `${deck.name}: ${stats.total} cards (${stats.newCount} new, ${stats.learning} learning, ${stats.review} in review), ${stats.dueNow} due now, ${stats.reviewsTotal} reviews logged${stats.againRate != null ? `, ${Math.round(stats.againRate * 100)}% marked Again` : ""}.`
    : "";

  return (
    <ModalShell title="Deck stats" subtitle={deck.name} onCancel={onClose}>
      {stats === null ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }} aria-live="polite">
          {[0, 1].map((i) => <div key={i} className="skeleton-block" style={{ height: 52 }} />)}
        </div>
      ) : (
        <>
          <div className="stat-grid" style={{ marginBottom: "0.75rem" }}>
            <div className="stat">
              <div className="stat-lbl"><Layers size={12} /> Cards</div>
              <div className="stat-val tnum">{stats.total}</div>
              <div className="stat-sub">{stats.newCount} new · {stats.learning} learning · {stats.review} in review</div>
            </div>
            <div className="stat">
              <div className="stat-lbl"><Clock size={12} /> Due now</div>
              <div className="stat-val tnum">{stats.dueNow}</div>
              <div className="stat-sub">{stats.dueNow === 0 ? "all scheduled ahead" : "ready to study"}</div>
            </div>
          </div>
          <div className="stat-grid">
            <div className="stat">
              <div className="stat-lbl"><BarChart2 size={12} /> Reviews logged</div>
              <div className="stat-val tnum">{stats.reviewsTotal}</div>
              <div className="stat-sub">on this device</div>
            </div>
            <div className="stat">
              <div className="stat-lbl"><RefreshCw size={12} /> Marked "Again"</div>
              <div className="stat-val tnum">{stats.againRate != null ? `${Math.round(stats.againRate * 100)}%` : "—"}</div>
              <div className="stat-sub">{stats.againRate != null ? "of your reviews" : "no reviews yet"}</div>
            </div>
          </div>
          <div className="row" style={{ justifyContent: "space-between", marginTop: "1rem", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>Computed on this device from your own review history — nothing sent.</span>
            {copyText && <CopyButton text={copyText} />}
          </div>
        </>
      )}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "1rem" }}>
        <button className="btn btn-pill" onClick={onClose}>Close</button>
      </div>
    </ModalShell>
  );
}

const fieldLabel: React.CSSProperties = { display: "block", fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.45rem" };

function ModalShell({ title, subtitle, children, onCancel, wide }: { title: string; subtitle?: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  // Esc closes; restore focus is browser-default. Scrim click closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="sheet-scrim" onClick={onCancel}>
      <div className="sheet sheet-center" onClick={(e) => e.stopPropagation()} style={{ width: wide ? "min(480px, calc(100% - 2rem))" : "min(400px, calc(100% - 2rem))", paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.1rem", gap: "0.6rem" }}>
          <div style={{ minWidth: 0 }}>
            <div className="serif" style={{ fontWeight: 600, fontSize: "1.2rem" }}>{title}</div>
            {subtitle && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>}
          </div>
          <button className="icon-btn" aria-label="Close" onClick={onCancel} style={{ flexShrink: 0, marginTop: -2 }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
